import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { ApiError, apiJson } from '@/lib/apiClient';
import { tenantsQueryKey } from '@/features/tenants/useTenants';

/**
 * Plans & billing (slice 20).
 *
 * Types mirror the wire contract in `api/src/modules/admin/routes.ts` (seam S1)
 * and are never re-declared from the screen's point of view.
 */

export type PlanTier = 'track' | 'fleet' | 'pro';

export interface TierSummary {
  tier: PlanTier;
  /** Integer cents. `min === max` only where §7 names a single price. */
  /** ONE price per tier — the 4 Sep 2026 card. The old min/max band is gone. */
  cents: number;
  tenantCount: number;
  vehicleCount: number;
}

export interface UnbilledDevice {
  traccarDeviceId: number;
  imeiMasked: string;
  name: string;
  model: string | null;
  /** The owning tenant, or `null` for a device belonging to no tenant at all. */
  tenantId: string | null;
  tenantName: string | null;
  reason: 'unallocated' | 'no_package';
  lastReportAt: string | null;
}

export interface DunningTenant {
  id: string;
  name: string;
  status: 'past_due' | 'suspended';
  monthlyRateCents: number | null;
  vehicleCount: number;
}

export interface PlansResponse {
  tiers: TierSummary[];
  unassignedTenantCount: number;
  unassignedVehicleCount: number;
  /**
   * 🔴 `null` MEANS "WE CANNOT SEE" AND MUST NOT RENDER AS "ALL CLEAR".
   *
   * The server returns `null` when the Traccar reader is absent or unreachable.
   * `[]` is a positive claim that every reporting device is billed — the whole
   * value of this panel is being trustworthy when it is empty, so the screen
   * renders an explicit unavailable state instead.
   */
  unbilled: UnbilledDevice[] | null;
  dunning: DunningTenant[];
}

export const plansQueryKey = ['admin', 'plans'] as const;

export function usePlans(): UseQueryResult<PlansResponse> {
  return useQuery({
    queryKey: plansQueryKey,
    queryFn: ({ signal }) => apiJson<PlansResponse>('/api/admin/plans', { signal }),
    // A 403 is a decision, not a hiccup.
    retry: (count, error) =>
      !(error instanceof ApiError && error.status >= 400 && error.status < 500) && count < 2,
  });
}

export interface UpdatePlanBody {
  plan: PlanTier;
  /** REQUIRED and nullable — never omitted. See the route's header. */
  monthlyRateCents: number | null;
}

export function useUpdatePlan(
  tenantId: string,
): UseMutationResult<unknown, Error, UpdatePlanBody> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdatePlanBody) =>
      apiJson(`/api/admin/tenants/${tenantId}/plan`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    // BOTH lists move: repricing changes a tier card, the MRR total on the
    // tenants screen, AND whether this tenant's devices are still unbilled.
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: plansQueryKey });
      void qc.invalidateQueries({ queryKey: tenantsQueryKey });
    },
  });
}

export function useUpdateStatus(
  tenantId: string,
): UseMutationResult<unknown, Error, { status: 'active' | 'past_due' | 'suspended' }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { status: 'active' | 'past_due' | 'suspended' }) =>
      apiJson(`/api/admin/tenants/${tenantId}/status`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: plansQueryKey });
      void qc.invalidateQueries({ queryKey: tenantsQueryKey });
    },
  });
}

/**
 * Cents → `$6.00`, or a range → `$8.00–12.00`.
 *
 * The ONE division by 100 on this screen. Money is integer cents everywhere
 * behind it and is formatted exactly once, at the edge.
 */
/**
 * A tier's price. Cents → dollars happens HERE and nowhere else.
 *
 * 🔴 Replaces `formatCentsRange`. There is no range any more: the tier decides
 * one price (Mahad, 4 Sep — *"in fleet i see $10 - 12, i dont know why, it
 * should be 10/vehicle/month"*). A per-tenant deal is an OVERRIDE shown against
 * the tenant, never a widening of the tier's own figure.
 */
export function formatCents(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

